import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { NotificacionDto } from '../../domain/notifications/notification.dto';
import { NotificationsRepository } from '../repositories/notifications.repository';

const POLL_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class NotificationsFacade {
  private readonly notificationsRepository = inject(NotificationsRepository);
  private readonly router = inject(Router);
  private timer: ReturnType<typeof setInterval> | null = null;

  readonly items = signal<NotificacionDto[]>([]);
  readonly unread = signal(0);
  readonly open = signal(false);
  readonly loading = signal(false);
  readonly loadError = signal(false);

  start(): void {
    if (this.timer) return;
    void this.refreshCount();
    this.timer = setInterval(() => void this.refreshCount(), POLL_MS);
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  toggle(): void {
    const next = !this.open();
    this.open.set(next);
    if (next) void this.refreshList();
  }

  close(): void {
    this.open.set(false);
  }

  async openItem(item: NotificacionDto): Promise<void> {
    this.close();
    if (item.recursoRuta) {
      void this.router.navigateByUrl(item.recursoRuta);
    }
    if (!item.leida) await this.markRead(item);
  }

  private async markRead(item: NotificacionDto): Promise<void> {
    try {
      await firstValueFrom(this.notificationsRepository.markRead(item.id));
    } catch {
      return;
    }
    this.items.update((current) => current.map((row) => (row.id === item.id ? { ...row, leida: true } : row)));
    this.unread.update((count) => Math.max(0, count - 1));
  }

  private async refreshCount(): Promise<void> {
    try {
      const result = await firstValueFrom(this.notificationsRepository.unreadCount());
      this.unread.set(result.noLeidas);
    } catch {
      return;
    }
  }

  private async refreshList(): Promise<void> {
    this.loading.set(true);
    this.loadError.set(false);
    try {
      this.items.set(await firstValueFrom(this.notificationsRepository.list()));
    } catch {
      this.loadError.set(true);
    } finally {
      this.loading.set(false);
    }
  }
}
